import '@shui/core/sh-tabs';

import { booleanAttribute, Component, input, output, ChangeDetectionStrategy } from '@angular/core';

@Component({
    selector: 'sh-tab-item',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.accessible-close-label]': 'accessibleCloseLabel() ?? null',
        '[attr.active]': 'active() ? "" : null',
        '[attr.badge]': 'badge() ?? null',
        '[attr.closable]': 'closable() ? "" : null',
        '[attr.disabled]': 'disabled() ? "" : null',
        '[attr.href]': 'href() ?? null',
        '[attr.icon]': 'icon() ?? null',
        '[attr.info]': 'info() ?? null',
        '[attr.label]': 'label() ?? null',
        '[attr.name]': 'name() ?? null',
        '[attr.neutral]': 'neutral() ? "" : null',
        '[attr.patient]': 'patient() ? "" : null',
        '[attr.size]': 'size() ?? null',
        '[attr.sticky]': 'sticky() ? "" : null',
        '(click)': 'clicked.emit($event)',
        '(closed)': 'closed.emit($event)'
    }
})
export class ShTabItemComponent {
    /**
     * If the `accessibleCloseLabel` property is not set, the icon will use the `type` to
     * dynamically set the `label`.
     */
    public readonly accessibleCloseLabel = input<string | number | undefined>(undefined);

    /**
     * Defines whether a Tab Item is active or not. The style will change to represent the active
     * state.
     */
    public readonly active = input(false, { transform: booleanAttribute });

    /** If set, will be shown as badge for tab item. */
    public readonly badge = input<string | number | undefined>(undefined);

    /** If set, close icon will be shown for tab item. */
    public readonly closable = input(false, { transform: booleanAttribute });

    /** If set, disabled styles will be applied */
    public readonly disabled = input(false, { transform: booleanAttribute });

    /** Defines a path to which navigation will occur on click. */
    public readonly href = input<string | number | undefined>(undefined);

    /** Defines an icon to be shown inside the Tab Item. */
    public readonly icon = input<string | number | undefined>(undefined);

    /** Shows an info text below the label. Only applicable if the patient property is true. */
    public readonly info = input<string | number | undefined>(undefined);

    /** Defines the label shown at the Tab Item. */
    public readonly label = input<string | number | undefined>(undefined);

    /** Will be displayed as a label if the tab is collapsed and the label property is undefined. */
    public readonly name = input<string | number | undefined>(undefined);

    /**
     * If set, neutral tab variantion does not use the primary orange color for highlighting & uses
     * a more subtle visual style. It has no effect on collapsed tab.
     */
    public readonly neutral = input(false, { transform: booleanAttribute });

    /**
     * Defines whether the Tab Item is of a patient type or not. If set to `true`, the icon property
     * will overwrite the default icon, and the info property will be available.
     */
    public readonly patient = input(false, { transform: booleanAttribute });

    /** Defines the size of an active tab. Supports two values l (23.5rem) and s (17.5rem). */
    public readonly size = input<string | number | undefined>(undefined);

    /**
     * If set to `true` the tab will not have the closing functionality (close icon will be hidden).
     */
    public readonly sticky = input(false, { transform: booleanAttribute });

    public readonly clicked = output<Event>();

    public readonly closed = output<Event>();
}
